import {forwardRef, useCallback, useEffect, useState} from "react";
import useTranslate from "platformed-components/i18n/useTranslate";
import {NumberInputField, NumberInputRoot} from "../ui/number-input";
import {ScalarValueInputProps} from "./types";

/**
 * Integer input component
 *
 * Features:
 * - Validates input on blur
 * - Returns null for empty input
 * - Syncs with external value when not focused
 */
const IntegerInput = forwardRef<HTMLInputElement, ScalarValueInputProps<number>>(
  ({value, onChange, placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    const effectivePlaceholder = placeholder ?? t("c.Enter_number");
    const initialValue = typeof value === "number" ? value.toString() : "";
    const [localValue, setLocalValue] = useState(initialValue);
    const [isFocused, setIsFocused] = useState(false);

    // Sync with prop changes only when not focused
    useEffect(() => {
      if (!isFocused) {
        const newValue = typeof value === "number" ? value.toString() : "";
        setLocalValue(newValue);
      }
    }, [value, isFocused]);

    const handleBlur = useCallback(() => {
      setIsFocused(false);
      if (localValue === "") {
        onChange(null);
      } else {
        const numValue = parseInt(localValue, 10);
        if (!isNaN(numValue)) {
          onChange(numValue);
        }
      }
    }, [localValue, onChange]);

    const handleFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    return (
      <NumberInputRoot
        width="100%"
        value={localValue}
        step={1}
        formatOptions={{maximumFractionDigits: 0}}
        onValueChange={details => setLocalValue(details.value)}
        disabled={disabled}
      >
        <NumberInputField
          ref={ref}
          placeholder={effectivePlaceholder}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
        />
      </NumberInputRoot>
    );
  },
);

IntegerInput.displayName = "IntegerInput";

export default IntegerInput;
